import { Typography, Grid, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import EditIcon from "@mui/icons-material/Edit";
import TextSection from "./TextSection";
import TableSection from "./TableSection";
import ChartSection from "./ChartSection";
import ImageSection from "./ImageSection";
import ImageTableSection from "./ImageTableSection";
import { useState } from "react";

export const useStyles = makeStyles()((theme) => ({
  subHeading1: {
    fontSize: "28px",
    color: theme.palette.secondary.main,
    marginBottom: "21px",
    textTransform: "uppercase",
    textDecoration: "underline",
  },
  subHeading2: {
    fontSize: "21px",
    fontFamily: "Roboto, sans-serif",
    fontWeight: "bold",
    lineHeight: 1.2,
    marginBottom: "10px",
    // capitalize the first letter of each word
    textTransform: "capitalize",
  },
  chartBox: {
    border: "1px solid #ddd",
    padding: "14px",
  },
}));

function PageSection(props: any): JSX.Element {
  console.log("PageSection props: ", props);
  const { classes } = useStyles();
  const url = window.location.pathname;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [jsonText, setJsonText] = useState("");

  let heading = props.section?.heading?.includes("Success")
    ? "Success, Challenges and Farmer Feedback"
    : props.section?.heading;

  // Priority order for known keys:
  // 1. type
  // 2. heading
  // 3. Among [text, chart_data, table, image] in order: text, chart_data, table, image
  // 4. subsection
  // 5. Then any unknown keys alphabetically
  const knownKeyPriority: Record<string, number> = {
    type: 0,
    heading: 1,
    text: 2,
    chart_data: 3,
    table: 4,
    image: 5,
    subsection: 6,
  };
  

  const reorderSectionKeys = (data: any): any => {
    if (Array.isArray(data)) {
      // Reorder each element in the array if they are objects
      return data.map((item) => (typeof item === "object" && item !== null ? reorderSectionKeys(item) : item));
    } else if (data && typeof data === "object") {
      // Sort keys according to priority
      const keys = Object.keys(data);

      // Separate known and unknown keys
      const knownKeys = keys.filter((k) => knownKeyPriority.hasOwnProperty(k));
      const unknownKeys = keys.filter((k) => !knownKeyPriority.hasOwnProperty(k));

      // Sort known keys by their priority, but handle the block of [text, chart_data, table, image]
      // We gave them separate priorities to ensure they come in the correct order.
      knownKeys.sort((a, b) => knownKeyPriority[a] - knownKeyPriority[b]);

      // Sort unknown keys alphabetically
      unknownKeys.sort();

      const sortedKeys = [...knownKeys, ...unknownKeys];

      // Rebuild the object with sorted keys and reorder subsections if present
      const newObj: any = {};
      for (const k of sortedKeys) {
        newObj[k] = reorderSectionKeys(data[k]);
      }
      return newObj;
    } else {
      // Primitive type
      return data;
    }
  };

  const handleOpenDialog = () => {
    console.log("PageSection Popup Opened: ", props);
    // Parse, reorder keys, and then stringify
    const reordered = reorderSectionKeys(props.section);
    setJsonText(JSON.stringify(reordered, null, 2));
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleSave = () => {
    console.log("PageSection Popup Saved content:", jsonText);
    setDialogOpen(false);
  };

  const handleCancel = () => {
    console.log("PageSection Popup cancelled");
    setDialogOpen(false);
  };

  const renderSectionByType: any = (section_data: any) => {
    let formattedHeading = section_data.heading
      ?.split(" ")
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    switch (section_data?.type) {
      case "text":
        return (
          <Grid className="sub-section">
            {section_data.text && <TextSection page={props.page}  content={section_data} />}
            {section_data.subsection?.map((subsection: any, index: number) => (
              <Grid key={index}>
                {subsection.type === "text" && (
                  <>
                    <hr />
                    <Typography className={classes.subHeading1}>{subsection.heading}</Typography>
                  </>
                )}
                {renderSectionByType(subsection)}
              </Grid>
            ))}
          </Grid>
        );
      case "table":
        return (
          <Grid className="sub-section">
            {section_data.table && <TableSection content={section_data.table} tableType={section_data.table_type} />}
            {section_data.subsection?.map((subsection: any, index: number) => (
              <Grid key={index}>{renderSectionByType(subsection)}</Grid>
            ))}
          </Grid>
        );
      case "chart":
        return (
          <Grid className={typeof section_data.chart_data === "string" ? "hide_secion" : "sub-section"} xs={12} style={{ margin: "auto" }}>
            {typeof section_data.chart_data === "string" ? (
              <>
                <hr />
                <Typography className={classes.subHeading1}>{formattedHeading}</Typography>
                {section_data.text ? <TextSection page={props.page}  content={section_data} /> : <Typography variant="body2">{section_data.chart_data}</Typography>} 
              </>
            ) : url.includes("labour_risks") ? (
              <ChartSection content={section_data} year={props.year} page={props.page} heading={formattedHeading} />
            ) : (
              <>
                <ChartSection content={section_data} year={props.year} page={props.page} heading={formattedHeading} />
              </>
            )}

            {section_data.subsection?.map((subsection: any, index: number) => (
              <Grid key={index}>{renderSectionByType(subsection)}</Grid>
            ))}
          </Grid>
        );
      case "image":
        return (
          <Grid className="sub-section">
            <ImageSection content={section_data} />
          </Grid>
        );
      case "image-table":
        return (
          <Grid className="sub-section">
            <ImageTableSection content={section_data} />
          </Grid>
        );
      default:
        return <Typography variant="body2">No Data</Typography>;
    }
  };

  const showHeading = 
    props.section.type !== "chart" &&
    heading &&
    !(heading.includes("Over Time") && props.section.subsection?.[0]?.chart_data === "No Data Found");

  return (
    <Grid
      className={heading?.includes("Over Time") && props.section.subsection?.[0]?.chart_data === "No Data Found" ? "hide_secion" : "section"}
      id={heading?.replace(/[\s,]+/g, "")}
    >

      {showHeading && (
        <Grid container justifyContent="space-between" alignItems="center">
          <Typography variant="h5">{heading}</Typography>
          {/* <IconButton onClick={handleOpenDialog} aria-label="Edit JSON">
            <EditIcon />
          </IconButton> */}
        </Grid>
      )}

      {renderSectionByType(props.section)}

      {heading === "Overview" && props.allSections}

      {/* Editable Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        sx={{ 
          "& .MuiTypography-root": {
            fontFamily: "'Courier New', monospace"
          } 
        }}
      >
        <DialogTitle>PageSection JSON Data</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={10}
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            variant="outlined"
            sx={{
              "& .MuiInputBase-input": {
                fontFamily: "monospace",
                fontSize: "12px",
                color: "#333",
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default PageSection;
